import { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import type { Timeout } from 'ahooks/lib/useRequest/src/types'
import cn from 'classnames'
import { produce } from 'immer'
import * as R from 'ramda'
import { useRef, useState } from 'react'
import { useContextSelector } from 'use-context-selector'

import { useProjectContext } from 'services/Store/Project/hooks'
import { getBlockId } from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'

import GroupingContext from '../../GroupingContext'
import Result from '../Result'
import Answer from './Answer'
import Group from './Group'
import * as s from './MobileElement.module.scss'

const MobileElement = () => {
  const styles = useContextSelector(GroupingContext, (state) => state.styles)
  const shuffledAnswers = useContextSelector(GroupingContext, (state) => state.shuffledAnswers)
  const validationMap = useContextSelector(GroupingContext, (state) => state.validationMap)
  const destination = useContextSelector(GroupingContext, (state) => state.destination)
  const testResult = useContextSelector(GroupingContext, (state) => state.testResult)
  const setDestination = useContextSelector(GroupingContext, (state) => state.setDestination)
  const setTestResult = useContextSelector(GroupingContext, (state) => state.setTestResult)
  const hasGroupImage = useContextSelector(GroupingContext, (state) => state.hasGroupImage)
  const elementId = useContextSelector(GroupingContext, (state) => state.elementId) || ''
  const setShuffledAnswers = useContextSelector(
    GroupingContext,
    (state) => state.setShuffledAnswers,
  )
  const groupWrapperClasses = cn(s.groups, { [s.tableView]: hasGroupImage })
  const [activeId, setActiveId] = useState('')
  const ref = useRef<number | null | Timeout>()
  const blockUuid = useProjectContext(getBlockId) || ''
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: blockUuid,
      elementType: CONFIG_ELEMENTS.grouping.type,
      elementId,
    },
  )

  const onAssign = (destId: string) => {
    if (activeId || !shuffledAnswers.length) {
      return
    }
    if (ref.current) {
      clearTimeout(ref.current)
    }

    setActiveId(destId)

    const answer = shuffledAnswers[shuffledAnswers.length - 1]?.value
    const answerLabel = shuffledAnswers[shuffledAnswers.length - 1]?.label
    setTestResult({ ...testResult, [answer]: validationMap[answer] === destId })
    const [firstCard, secondCard] = Object.values(validationMap)
    const data = entityAnalyticsConfig.interacted({
      resultExtensions: {
        itemLabel: answerLabel,
        result: validationMap[answer] === destId,
        validationCardFirstId: firstCard,
        validationCardSecondId: secondCard,
      },
      objectExtensions: { itemId: answer, ...baseObjectExtensions },
      elementId,
    })
    sendXapiData(data)
    const newDest = produce(destination, (draft) => {
      const dest = draft.find((e) => e.value === destId)
      dest?.items.push(shuffledAnswers[shuffledAnswers.length - 1])
    })

    setDestination(newDest)
    const shuffled = shuffledAnswers.filter((e) => e.value !== answer)

    ref.current = setTimeout(() => {
      setActiveId('')
      setShuffledAnswers(shuffled)
    }, 1000)
  }

  return (
    <div className={s.root}>
      {shuffledAnswers.length === 0 && <Result />}
      {shuffledAnswers.length > 0 && (
        <div aria-label='Answer cards' className={s.answers} role='list'>
          {shuffledAnswers.map((item) => (
            <Answer item={item} key={item.value} />
          ))}
        </div>
      )}
      <div
        aria-label='Drop zones'
        className={groupWrapperClasses}
        role='list'
        style={{ gap: styles.indents.gap, marginTop: styles.indents.marginTop }}
      >
        {destination.map((item) => (
          <Group
            activeId={activeId}
            group={item}
            isCorrect={Boolean(R.last(R.values(testResult)))}
            key={item.value}
            onAssign={onAssign}
          />
        ))}
      </div>
    </div>
  )
}

export default MobileElement
